import { gql, type OperationVariables } from '@apollo/client';
import type ICompanySettingsDTO from '@sharedInterfaces/ICompanySettings';
import Company from '@src/Objects/Company';

import GraphQL from '../graphQL';

const timeSyncStatisGql = `{
    status
    lastUpdateProjects
    lastUpdateCustomer
    lastUpdateEmployeeAbsence
    lastUpdateEmployeeTimesLastYear
    lastUpdateEmployeeTimesThisYear
}`;

const permissionEntitiesActions = `{ 
    Add
    Edit
    Delete
    Retrieve
    RetrieveOwn
    RetrieveSalesData
    RetrieveAttachments
    RetrieveOwnAttachments
    EditAttachments
}`;
export const entitiesGql = `
entities {
    Skills ${permissionEntitiesActions}
    Certificates ${permissionEntitiesActions}
    Competencies ${permissionEntitiesActions}
    Products ${permissionEntitiesActions}
    Projects ${permissionEntitiesActions}
    Companies ${permissionEntitiesActions}
    SkillCategories ${permissionEntitiesActions}
    Opportunities ${permissionEntitiesActions}
    OrgUnits ${permissionEntitiesActions}
    Roles ${permissionEntitiesActions}
    EmployeeTalkTemplates ${permissionEntitiesActions}
    EmployeeTalks ${permissionEntitiesActions}
}`;

export const styleGql = `{
    primary
    onPrimary
    secondary
    onSecondary
    logoIdLight
    logoIdDark
}`;

export const companySettingsGql = `
 {
    companyId,
    name,
    employees{
        id
        userName
        firstname
        lastname
        gender
        email
        permissions
        {
            admin
        }
        permissionGroup{
            id
            title
        }
    },
    style
    ${styleGql},
    OUs{
        id
        parentOU
        title
        leader
        employees
        adGroupId
    },
    level{
        level
        title
        description
        helperText
    },
    permissionGroups{
        id
        title
        ${entitiesGql}
    }
    defaultPermissionGroup{
        id
        title
    }
    allowedMailEndings
    timeSync
    clockodoSync
    {
        email
    }
    clockodoStatis
    ${timeSyncStatisGql}
    zepStatis
    ${timeSyncStatisGql}
    zepInstance
    calculations
    {
        forecastMonthCount
        workedMonthCount
    }
    localisation
    {
        country
        subCountry
        language
    }
    industry
    holidaySettings
    {
        source
        holidays
        {
            year
            holidays
            {
                date
                name
                isFullDay
            }
        }
    }
    emailSettings{
        emailTransferOption
        exchangeMailbox
        forecastCycle
        forecastText
        sendForecastMail
        sendUpdateReminderMail
        sendCertificateReminder
    }
    apiKeys{
        id
        note
        createdAt
        createdBy
    }
    microsoftSettings{
        tenantId
        clientId
        certificateThumbprint
        certificateStatus
        connectionState
    }
    globalEmailSupport
    slack
    {
        tokenIsSet
        signingSecretIsSet
    }
}`;
const q = gql`
query {
    companySettings
    ${companySettingsGql}
},
`;

/**
 * Retrieves the settings of a company.
 *
 * @param {string} companyId - The ID of the company.
 * @param {(company: Company) => void} callback - The callback function to be executed with the company object.
 *
 * @returns {Promise<{ companySettings: ICompanySettings }> - A promise that resolves with the company settings.
 */
export default function getCompanySettings(_companyId: string, callback?: (company: Company) => void) {
    const variables: OperationVariables = {};
    return GraphQL.query<{ companySettings: ICompanySettingsDTO }>(q, variables, (obj) => {
        if (!obj) throw new Error('Error Load Company');

        const result = new Company(obj.companySettings);
        callback?.(result);
        return result;
    });
}
